import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Globe2, Plane, Cpu, Bot, ShoppingBag, Building2,
  ArrowUpRight, ChevronLeft, ChevronRight, Sparkles, Shield, TrendingUp, Eye
} from 'lucide-react';

/**
 * ExpeditionBlock — "Global Expeditions Hub"
 * Posiciona al CEO Mario Pino Corona como Global Tech Scout.
 * Estética: ALLCOM Premium B&W con glassmorphism. Imágenes B&W → color en hover.
 * Renderizado al final de /empresa/noticias.
 */

const EXPEDITIONS = [
  {
    id: 'london-rts',
    code: 'LDN-26',
    fair: 'Retail Technology Show',
    city: 'Londres, Reino Unido',
    year: '2026',
    icon: ShoppingBag,
    image: '/expeditions/exp-london-rts.png',
    focus: 'Evolución del Retail Europeo y Transacción Invisible.',
    insight: 'Alianzas estratégicas para soluciones de Frictionless Shopping en Chile.',
    chile: [
      'Reducción de costos operativos mediante visión por computador.',
      'Cumplimiento normativo estricto con estándares europeos GDPR.',
    ],
  },
  {
    id: 'computex-tpe',
    code: 'TPE-24',
    fair: 'COMPUTEX',
    city: 'Taipéi, Taiwán',
    year: '2024',
    icon: Cpu,
    image: '/expeditions/exp-computex-tpe.png',
    focus: 'AI Hardware y Edge Computing.',
    insight: 'Validación de procesadores de IA para el motor MuniAI.',
    chile: [
      'Procesadores Edge AI auditados, listos para despliegue en municipios.',
      'Integración con normativa chilena de protección de datos.',
    ],
  },
  {
    id: 'canton-sha',
    code: 'SHA-24',
    fair: 'China Shop & Canton Fair',
    city: 'Guangzhou / Shanghái',
    year: '2024',
    icon: Bot,
    image: '/expeditions/exp-canton-sha.png',
    focus: 'Automatización masiva y robótica de servicio.',
    insight: 'Alianzas directas con fabricantes LED y de robótica avanzada.',
    chile: [
      'Acceso preferencial a manufactura LED de vanguardia (división AVX).',
      'Transferencia tecnológica con QA chileno antes del despliegue.',
    ],
  },
  {
    id: 'nrf-nyc',
    code: 'NYC-24',
    fair: 'NRF · Retail\u2019s Big Show',
    city: 'Nueva York, USA',
    year: '2024',
    icon: TrendingUp,
    image: '/expeditions/exp-nrf-nyc.png',
    focus: 'Estrategia global de Consumer Experience.',
    insight: 'Aplicación de Big Data para personalización en tiempo real.',
    chile: [
      'Dashboards ejecutivos con benchmarking sectorial internacional.',
      'Estándares de experiencia replicables en retail nacional.',
    ],
  },
  {
    id: 'chile-scl',
    code: 'SCL-26',
    fair: 'Expediciones Chile',
    city: 'Santiago, Chile',
    year: '2026',
    icon: Building2,
    image: '/expeditions/exp-chile-scl.png',
    focus: 'Adaptación de tendencias globales a la realidad y normativa local.',
    insight: 'Aterrizaje regulatorio: Ley 20.285 de Acceso a la Información Pública y estándares de Transparencia.',
    chile: [
      'Soluciones globales certificadas para el contexto fiscal chileno.',
      'Cumplimiento integral con MuniAI y portales de transparencia.',
    ],
  },
];

const ROADMAP = [
  {
    icon: Cpu,
    title: 'IA en el punto de venta',
    desc: 'Integración total de inteligencia artificial directamente en el POS, reduciendo fricción transaccional.',
  },
  {
    icon: Shield,
    title: 'MuniAI · Transparencia municipal',
    desc: 'Democratización del acceso a información pública mediante agentes IA al servicio de la ciudadanía.',
  },
  {
    icon: Eye,
    title: 'AVX · Ecosistemas visuales inteligentes',
    desc: 'Hardware LED de vanguardia conectado al stack ALLCOM para retail, eventos y arquitectura.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.21, 0.61, 0.35, 1] } },
};

export default function ExpeditionBlock() {
  const [activeIdx, setActiveIdx] = useState(0);
  const trackRef = React.useRef(null);
  const programmaticLockRef = React.useRef(false);

  const scrollToIdx = (idx) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(`[data-testid="expedition-card-${EXPEDITIONS[idx].id}"]`);
    if (!card) return;
    // Align the target card to the LEFT of the visible track (with container padding compensation)
    const targetScroll = card.offsetLeft - el.offsetLeft;
    programmaticLockRef.current = true;
    el.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
    setActiveIdx(idx);
    // Release the lock after the smooth scroll settles
    setTimeout(() => { programmaticLockRef.current = false; }, 700);
  };

  const scrollBy = (dir) => {
    const next = Math.max(0, Math.min(EXPEDITIONS.length - 1, activeIdx + dir));
    scrollToIdx(next);
  };

  // Update activeIdx based on scroll position — only if the current active card is scrolled out of view
  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf = null;
    const onScroll = () => {
      if (programmaticLockRef.current) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        // Check if the current active card is still (at least partially) visible
        const trackLeft = el.scrollLeft;
        const trackRight = trackLeft + el.clientWidth;
        const activeCard = el.querySelector(`[data-testid="expedition-card-${EXPEDITIONS[activeIdx].id}"]`);
        if (activeCard) {
          const cardLeft = activeCard.offsetLeft - el.offsetLeft;
          const cardRight = cardLeft + activeCard.clientWidth;
          // Consider "in view" if at least 50% of the card is visible
          const visibleLeft = Math.max(cardLeft, trackLeft);
          const visibleRight = Math.min(cardRight, trackRight);
          const visibleWidth = Math.max(0, visibleRight - visibleLeft);
          if (visibleWidth >= activeCard.clientWidth * 0.5) return; // still visible enough → don't change
        }
        // Active card lost view → switch to the card whose center is closest to the track center
        const center = el.scrollLeft + el.clientWidth / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        EXPEDITIONS.forEach((exp, i) => {
          const c = el.querySelector(`[data-testid="expedition-card-${exp.id}"]`);
          if (!c) return;
          const cCenter = c.offsetLeft - el.offsetLeft + c.clientWidth / 2;
          const d = Math.abs(cCenter - center);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        });
        setActiveIdx(bestIdx);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [activeIdx]);

  return (
    <section
      className="relative bg-[#050E1F] text-white overflow-hidden"
      data-testid="expedition-block"
    >
      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full bg-white/5 backdrop-blur-xl border border-white/10"
              data-testid="expedition-eyebrow"
            >
              <Globe2 className="w-3.5 h-3.5 text-white/70" strokeWidth={1.5} />
              <span className="text-[10px] font-mono uppercase tracking-[0.32em] text-white/70">
                Global Expeditions · Mario Pino Corona
              </span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05] mb-4"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="expedition-title"
            >
              Donde nace la tecnología,<br />
              <span className="text-white/40">ALLCOM ya estaba ahí.</span>
            </h2>
            <p className="text-base text-white/60 leading-relaxed">
              Nuestro CEO viaja a los epicentros mundiales de innovación retail e inteligencia artificial.
              Cada expedición se traduce en alianzas, hardware validado y estrategia aplicada al cliente chileno.
            </p>
          </div>

          {/* Slider controls — desktop */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 rounded-full border border-white/15 hover:border-white/40 flex items-center justify-center transition-colors"
              data-testid="expedition-prev"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-11 h-11 rounded-full border border-white/15 hover:border-white/40 flex items-center justify-center transition-colors"
              data-testid="expedition-next"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Slider — horizontal scrollable */}
        <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
          <div
            id="expedition-track"
            ref={trackRef}
            className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 px-4 sm:px-6 lg:px-8"
            style={{ scrollbarWidth: 'none' }}
            data-testid="expedition-track"
          >
            <style>{`#expedition-track::-webkit-scrollbar{display:none}`}</style>
            {EXPEDITIONS.map((exp, i) => (
              <ExpeditionCard
                key={exp.id}
                expedition={exp}
                index={i}
                isActive={activeIdx === i}
                onActivate={() => setActiveIdx(i)}
              />
            ))}
          </div>
        </div>

        {/* Mobile controls + dots indicator (all breakpoints) */}
        <div className="flex items-center justify-center gap-4 mt-6" data-testid="expedition-dots-wrapper">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={activeIdx === 0}
            className="lg:hidden w-10 h-10 rounded-full border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            data-testid="expedition-prev-mobile"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2" role="tablist" aria-label="Expediciones">
            {EXPEDITIONS.map((exp, i) => (
              <button
                key={exp.id}
                type="button"
                onClick={() => scrollToIdx(i)}
                className={`h-1.5 rounded-full transition-all ${activeIdx === i ? 'w-8 bg-white' : 'w-1.5 bg-white/25 hover:bg-white/50'}`}
                aria-label={`Ir a expedición ${exp.code}`}
                aria-selected={activeIdx === i}
                role="tab"
                data-testid={`expedition-dot-${i}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={activeIdx === EXPEDITIONS.length - 1}
            className="lg:hidden w-10 h-10 rounded-full border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            data-testid="expedition-next-mobile"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Future Roadmap */}
        <div className="mt-24 pt-16 border-t border-white/10" data-testid="expedition-roadmap">
          <div className="mb-12 max-w-2xl">
            <p className="text-[10px] font-mono uppercase tracking-[0.32em] text-white/50 mb-4">
              Future Roadmap · ALLCOM 3.0
            </p>
            <h3
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              Lo que viene en los próximos 24 meses.
            </h3>
          </div>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10">
            {ROADMAP.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-[#050E1F] p-8 sm:p-10"
                  data-testid={`expedition-roadmap-${i}`}
                >
                  <Icon className="w-7 h-7 mb-6 opacity-80" strokeWidth={1.4} />
                  <h4 className="text-lg font-semibold mb-3 leading-snug" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {r.title}
                  </h4>
                  <p className="text-sm text-white/55 leading-relaxed">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const ExpeditionCard = ({ expedition, index, isActive, onActivate }) => {
  const Icon = expedition.icon;
  return (
    <motion.article
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      tabIndex={0}
      className="group relative shrink-0 w-[320px] sm:w-[380px] lg:w-[420px] snap-start rounded-3xl overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-white/30 focus:border-white/40 transition-all duration-500 outline-none"
      data-testid={`expedition-card-${expedition.id}`}
    >
      {/* Image — B&W → color on hover */}
      <div className="relative aspect-[4/3] overflow-hidden bg-black">
        <img
          src={expedition.image}
          alt={`${expedition.fair} · ${expedition.city}`}
          className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-focus:grayscale-0 transition-all duration-700 group-hover:scale-105"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.opacity = '0.2'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050E1F] via-[#050E1F]/30 to-transparent" />

        {/* Metallic badge */}
        <div
          className="absolute top-4 right-4 px-3 py-1.5 rounded-md text-[10px] font-mono font-bold tracking-[0.18em] uppercase shadow-lg"
          style={{
            background: 'linear-gradient(135deg, #d8dde4 0%, #f4f6fa 35%, #b9c0cb 100%)',
            color: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.5)',
            textShadow: '0 1px 0 rgba(255,255,255,0.4)',
          }}
          data-testid={`expedition-badge-${expedition.code}`}
        >
          {expedition.code}
        </div>

        {/* Year + icon */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center">
            <Icon className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-white/60">{expedition.year}</div>
            <div className="text-xs font-semibold text-white">{expedition.city}</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-7 lg:p-8">
        <h3
          className="text-xl font-semibold mb-3 leading-tight"
          style={{ fontFamily: 'Syne, sans-serif' }}
        >
          {expedition.fair}
        </h3>

        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/40 mb-1.5">Foco</div>
            <p className="text-sm text-white/80 leading-relaxed">{expedition.focus}</p>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/40 mb-1.5">Insight clave</div>
            <p className="text-sm text-white/70 leading-relaxed">{expedition.insight}</p>
          </div>

          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-white/60" strokeWidth={1.5} />
              <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/60">Estrategia para Chile</span>
            </div>
            <ul className="space-y-1.5">
              {expedition.chile.map((c, i) => (
                <li key={i} className="text-xs text-white/70 leading-relaxed flex items-start gap-2">
                  <span className="text-white/40 mt-0.5">·</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.article>
  );
};
